import cookies from 'js-cookie';
import React, {Component} from 'react';
import {Redirect, withRouter, RouteComponentProps} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';
import qs from 'qs';

import {Market, MarketInfoProps} from 'etc/types';

import {MARKET_DATA} from './marketConfig';
import {LOCALES} from './localeConfig';

const MARKET_REGEX = /^\/(\w{2}(?:-\w{2})?)\//; // for instance, ca-fr
const DEFAULT_MARKET = 'us';

const softLaunchOverride: {market: {[key: string]: Market}; dir?: string} = {
  market: {},
  dir: undefined,
};

const getMarketData = (market: string) => softLaunchOverride.market[market] || MARKET_DATA[market];

export const MarketInfoContext = React.createContext({});

export const withMarketInfo = <P extends MarketInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof MarketInfoProps>) => (
    <MarketInfoContext.Consumer>
      {marketInfo => <Comp {...(props as P)} marketInfo={marketInfo} />}
    </MarketInfoContext.Consumer>
  );
};

export const getLocaleForMarket = (market: string) => getMarketData(market).locale;

const setupMarketOverride = () => {
  const queryParams = qs.parse(window.location.search, {ignoreQueryPrefix: true});
  if (!queryParams.softlaunch) {
    return;
  }

  const [market, locale = market, dir = 'ltr'] = queryParams.softlaunch.split(':');

  softLaunchOverride.market = {
    [market]: {
      locale,
      country: market,
    },
  };
  softLaunchOverride.dir = dir;
};

const getMarketFromUrl = () => {
  const regMarket = window.location.pathname.match(MARKET_REGEX);

  if (regMarket && regMarket[1] && getMarketData(regMarket[1])) {
    return regMarket[1];
  }
  return '';
};

const getMarketFromCookie = () => {
  const cookie = cookies.getJSON('spot');
  if (cookie && cookie.m && getMarketData(cookie.m)) {
    return cookie.m;
  }
  return '';
};

export const initiateMarket = () => {
  setupMarketOverride();

  const urlMarket = getMarketFromUrl();
  if (urlMarket) {
    return urlMarket;
  }

  return getMarketFromCookie() || DEFAULT_MARKET;
};

interface MarketInfoProviderProps extends RouteComponentProps {
  market: string;
}

class MarketInfoProvider extends Component<MarketInfoProviderProps> {
  hasProperMarket = false;

  render() {
    const {market, children} = this.props;

    // If we know we got a proper market setup, no need to check it anymore
    if (!this.hasProperMarket) {
      const marketInUrl = getMarketFromUrl();

      if (marketInUrl !== market) {
        const redirectUrl = window.location.pathname + window.location.search;
        return <Redirect to={redirectUrl} />;
      }

      this.hasProperMarket = true;
    }

    const contextValue = {
      market,
      phonePrefix: '',
      ...getMarketData(market),
    };

    return (
      <>
        <Helmet
          htmlAttributes={{
            lang: contextValue.locale,
            // The locales file is a special case since it is .js used from Gulp for translation syncs
            dir: softLaunchOverride.dir || (LOCALES as any)[contextValue.locale].dir,
          }}
        />

        <MarketInfoContext.Provider value={contextValue}>{children}</MarketInfoContext.Provider>
      </>
    );
  }
}

// MarketInfoProvider uses withRouter to make the <Redirect> work (it rerenders itself after the redirect)
export default withRouter(MarketInfoProvider);
